import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { 
  LocationOn as LocationIcon,
  LocalShipping as TruckIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API } from '../API/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Logo TMS
const TMSLogo = '/logo-tms.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, {
        email,
        password,
      });

      if (response.data && response.data.token) {
        const roleName = response.data.role_name?.toLowerCase() || 
                        response.data.user?.role?.name?.toLowerCase() || 
                        '';
        const roles = response.data.roles || [];
        
        const isClient = roleName === 'client' || 
                        roles.includes('client') ||
                        roles.some(r => r && r.toLowerCase() === 'client');
        
        console.log('🔍 Login Debug:', {
          roleName,
          roles,
          isClient,
          userRole: response.data.user?.role,
          client_id: response.data.user?.client_id,
        });
        
        if (!isClient) {
          setError('Accès réservé aux clients. Veuillez utiliser le portail principal.');
          return;
        }
        
        if (!response.data.user?.client_id) {
          setError('Votre compte client n\'est pas correctement configuré. Veuillez contacter l\'administrateur.');
          return;
        }
        
        const userRole = response.data.role || response.data.user?.role_id;
        const authData = {
          isAuthenticated: true,
          token: response.data.token,
          user: response.data.user,
          role: userRole,
          expiresAt: response.data.expires_at || response.data.expiresAt,
          roles: ['client'],
        };

        sessionStorage.setItem('authLocalstorage', JSON.stringify(authData));
        
        toast.success('Connexion réussie');
        navigate('/dashboard');
      } else {
        setError('Réponse invalide du serveur');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      let errorMessage = 'Erreur de connexion. Vérifiez vos identifiants.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        
        if (err.response.data.debug) {
          console.error('Debug info:', err.response.data.debug);
          errorMessage += ' (Voir la console pour plus de détails)';
        }
      } else if (err.response?.status === 403) {
        errorMessage = 'Accès refusé. Vérifiez que votre compte est correctement configuré.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#f8fafc',
      }}
    >
      {/* Panel gauche - Branding et informations */}
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Logo TMS */}
          <Box sx={{ mb: 6 }}>
            <Box
              component="img"
              src={TMSLogo}
              alt="TMS Logo"
              sx={{
                height: 70,
                maxWidth: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1) drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
              onError={(e) => {
                // Fallback si le logo n'est pas trouvé
                e.target.style.display = 'none';
                const fallback = e.target.parentElement;
                if (fallback) {
                  const fallbackDiv = document.createElement('div');
                  fallbackDiv.style.cssText = 'font-size: 36px; font-weight: 700; color: white; letter-spacing: 3px; text-shadow: 0 2px 4px rgba(0,0,0,0.2);';
                  fallbackDiv.textContent = 'TMS';
                  fallback.appendChild(fallbackDiv);
                }
              }}
            />
          </Box>

          {/* Titre */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: 'white',
              mb: 1,
              letterSpacing: '-0.03em',
              fontSize: { md: '2.5rem', lg: '3rem' },
            }}
          >
            Portail Client
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              color: 'rgba(255, 255, 255, 0.9)',
              mb: 4,
              fontSize: '1.1rem',
            }}
          >
            Suivez vos expéditions en temps réel
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              lineHeight: 1.8,
              fontSize: '1rem',
              maxWidth: '90%',
              mb: 4,
            }}
          >
            Accédez à votre espace client pour suivre l'état de vos expéditions, consulter les détails de vos voyages, télécharger vos documents de livraison et visualiser le suivi GPS en temps réel de vos camions.
          </Typography>

          {/* Avantages */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <LocationIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 500 }}>
                Suivi GPS en temps réel
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <TruckIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 500 }}>
                Historique complet de vos expéditions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <DownloadIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 500 }}>
                Téléchargement des documents de livraison
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.75rem',
              display: 'block',
              mb: 1,
            }}
          >
            © Copyright 2026 TMS. Tous droits réservés.
          </Typography>
          <Link
            href="mailto:it.ideumagency@gmail.com"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.75rem',
              textDecoration: 'underline',
              '&:hover': {
                color: 'white',
              },
            }}
          >
            Besoin d'aide ? Contactez le support
          </Link>
        </Box>
      </Box>

      {/* Panel droit - Formulaire de connexion */}
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 3, sm: 4, md: 6 },
          backgroundColor: '#ffffff',
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ maxWidth: 480, mx: 'auto' }}>
            {/* Salutation */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                mb: 1,
                letterSpacing: '-0.02em',
              }}
            >
              Bonjour!
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#1f2937',
                mb: 4,
                letterSpacing: '-0.015em',
              }}
            >
              Se connecter
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 1,
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                Remplir formulaire
              </Typography>

              <TextField
                fullWidth
                label="Email"
                placeholder="votre.email@example.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
                autoComplete="email"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3c2151',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                        borderColor: '#3c2151',
                      },
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Mot de passe"
                type="password"
                placeholder="Taper votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                margin="normal"
                autoComplete="current-password"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3c2151',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                        borderColor: '#3c2151',
                      },
                    },
                  },
                }}
              />

              {/* reCAPTCHA placeholder */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  backgroundColor: '#f9fafb',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    border: '2px solid #6b7280',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 0.5,
                      backgroundColor: '#3c2151',
                      display: 'none',
                    }}
                    className="recaptcha-check"
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Je ne suis pas un robot
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Box
                    component="img"
                    src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
                    alt="reCAPTCHA"
                    sx={{ height: 24, opacity: 0.7 }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              </Box>

              {/* Lien mot de passe oublié */}
              <Box sx={{ mb: 3, textAlign: 'right' }}>
                <Link
                  href="#"
                  sx={{
                    color: '#3c2151',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#2a173a',
                    },
                  }}
                >
                  Mot de passe oublié?
                </Link>
              </Box>

              {/* Bouton de connexion */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  letterSpacing: '1px',
                  background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                  boxShadow: '0 2px 8px rgba(60, 33, 81, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2a173a 0%, #3c2151 100%)',
                    boxShadow: '0 4px 12px rgba(60, 33, 81, 0.4)',
                  },
                  '&:disabled': {
                    background: '#9ca3af',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
